import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { LinkSimple, Baby, Phone, Heart } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import ListenButton from "../components/ListenButton";
import EmergencyButton from "../components/EmergencyButton";
import { RichText } from "../components/RichText";
import { PREMA_BY_ID } from "../data/prematuriteData";
import { trackEvent } from "../lib/events";

export default function PremaFiche() {
  const { id } = useParams();
  const fiche = PREMA_BY_ID[id];

  useEffect(() => {
    if (fiche) trackEvent("fiche_read", `prema/${fiche.id}`);
  }, [fiche]);

  if (!fiche) {
    return (
      <div>
        <PageHeader title="Introuvable" back />
        <p className="px-6 text-[15px] text-[#36302B]/60">Cette fiche n'existe pas ou plus.</p>
        <Link to="/prematurite" className="mt-4 block px-6 text-[14px] font-bold text-[#7E70AE]">
          Retour à l'espace prématurité
        </Link>
      </div>
    );
  }

  const listenItem = {
    title: fiche.title,
    category: fiche.tabLabel,
    read_time: "",
    content: `${fiche.intro}\n${fiche.content}`,
  };

  return (
    <div>
      <PageHeader title="Naissance prématurée" back />

      <article className="px-6">
        <div className="mb-4 overflow-hidden rounded-[24px] shadow-[0_6px_24px_rgba(54,48,43,0.08)]">
          <img
            src={`/prema/${fiche.id}.jpg`}
            alt={fiche.title}
            data-testid="prema-fiche-header-image"
            className="aspect-[16/9] w-full object-cover"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>

        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#9b8ec4]/12 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#7E70AE]">
          <Baby size={13} weight="fill" />
          {fiche.tabLabel}
        </span>

        <h2 className="font-display mt-3 text-[26px] font-medium leading-tight text-[#36302B]">{fiche.title}</h2>

        <p className="mt-3 rounded-2xl bg-[#F2EDE4] px-5 py-4 font-display text-[15px] italic leading-relaxed text-[#36302B]/85">
          {fiche.intro}
        </p>

        <div className="mt-5">
          <ListenButton item={listenItem} isPrema phaseLabel={fiche.tabLabel} />
        </div>

        <div className="mt-4">
          <RichText content={fiche.content} />
        </div>

        {fiche.terms && fiche.terms.length > 0 && (
          <div className="mt-6 rounded-3xl border border-[#9b8ec4]/25 bg-[#9b8ec4]/8 p-5">
            <p className="mb-3 text-[12px] font-bold uppercase tracking-wide text-[#7E70AE]">Les mots expliqués</p>
            <div className="space-y-2.5">
              {fiche.terms.map((t, i) => (
                <p key={i} className="text-[13.5px] leading-relaxed text-[#36302B]/80">
                  <strong className="text-[#36302B]">{t.t}</strong> — {t.d}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Urgences contextuelles */}
        {fiche.emergency === "samu" && (
          <a
            href="tel:15"
            data-testid="emergency-15-btn"
            className="mt-6 flex items-center gap-3 rounded-3xl bg-[#B56A63] px-5 py-4 text-white shadow-sm"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20">
              <Phone size={22} weight="fill" />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-extrabold leading-tight">Urgence vitale ? Appelez le 15 (SAMU)</p>
              <p className="text-[12.5px] text-white/85">En cas de signe de gravité, ne restez pas seule : composez le 15.</p>
            </div>
          </a>
        )}

        {fiche.emergency === "3114" && (
          <div className="mt-6">
            <EmergencyButton />
          </div>
        )}

        {fiche.emergency === "deuil" && (
          <div className="mt-6 rounded-3xl border border-[#9CAEA9]/30 bg-[#9CAEA9]/10 p-5" data-testid="deuil-resources">
            <div className="mb-2 flex items-center gap-2">
              <Heart size={20} color="#88A096" weight="duotone" />
              <p className="font-display text-[16px] font-medium text-[#36302B]">Vous n'êtes pas seule</p>
            </div>
            <p className="text-[13.5px] leading-relaxed text-[#36302B]/75">
              Associations de deuil périnatal : <strong>AGAPA</strong>, <strong>Petite Empreinte</strong>,
              <strong> SPAMA</strong>.
            </p>
            <a
              href="tel:0972394050"
              data-testid="sos-amitie-btn"
              className="mt-3 flex items-center gap-3 rounded-2xl bg-[#88A096] px-4 py-3 text-white"
            >
              <Phone size={20} weight="fill" />
              <div>
                <p className="text-[14px] font-bold">SOS Amitié · 09 72 39 40 50</p>
                <p className="text-[12px] text-white/85">Écoute, 24h/24, 7j/7</p>
              </div>
            </a>
          </div>
        )}

        {fiche.sources && fiche.sources.length > 0 && (
          <div className="mt-6 rounded-3xl bg-[#F2EDE4] p-5">
            <p className="mb-3 text-[12px] font-bold uppercase tracking-wide text-[#36302B]/50">Sources</p>
            <div className="space-y-2">
              {fiche.sources.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`prema-source-${i}`}
                  className="flex items-center gap-2 text-[13.5px] font-semibold text-[#7E70AE]"
                >
                  <LinkSimple size={16} />
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-[12px] leading-relaxed text-[#36302B]/45">
          Ces informations sont générales et ne remplacent pas l'avis de votre équipe médicale. En cas
          d'urgence vitale, appelez le 15.
        </p>
      </article>
    </div>
  );
}
